import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { ENABLE_REACHABILITY } from '../../../../../shared/featureFlags/flagNames';
import { Exploitability } from '../../../../../shared/vulns/types';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { FeatureFlagRequired } from '../../../generic';
import { Header } from '../../../shared/vulns/Drawer';
import CVEBadge from '../../IssuesInbox/IssueItem/CVEBadge';
import EpssBadge from '../../IssuesInbox/IssueItem/EpssBadge';
import FixBadge from '../../IssuesInbox/IssueItem/FixBadge';
import KnownExploitBadge from '../../IssuesInbox/IssueItem/KnownExploitBadge';
import ReachabilityBadge from '../../IssuesInbox/IssueItem/ReachabilityBadge';
import { areAnyProjectsReachable } from '../../utils';
import type { VulnIssue } from '../../../../../shared/issuesV2';

/**
 * VulnerabilityIssueDetailsHeader displays the title, cve, cvss, etc.
 * at the top of the vuln drawer.
 */
const VulnerabilityIssueDetailsHeader: VoidFunctionComponent = () => {
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails) as VulnIssue;
  const locatorParts = useSelector(IssuesV2Store.selectors.getIssueDetailsSourceLocatorParts);
  const isLinuxDep = useSelector(IssuesV2Store.selectors.getIsLinuxDep);
  if (!issueDetails) {
    return null;
  }
  const { exploitability, remediation } = issueDetails;
  const shouldShowFixBadge = remediation && !isLinuxDep;

  const isReachable = areAnyProjectsReachable(issueDetails.projects);

  return (
    <Header
      title={issueDetails.title}
      cvss={issueDetails.cvss}
      locatorParts={locatorParts}
      badgesSlot={
        <>
          {issueDetails.epss && (
            <EpssBadge tooltipId={`epss-badge-${issueDetails.id}`} epss={issueDetails.epss} showTooltip={false} />
          )}
          <FeatureFlagRequired flagName={ENABLE_REACHABILITY}>
            {isReachable && (
              <ReachabilityBadge showTooltip={false} />
            )}
          </FeatureFlagRequired>
          {shouldShowFixBadge && (
            <FixBadge
              version={issueDetails.remediation.partialFix}
              distance={issueDetails.remediation.partialFixDistance}
              showTooltip={false}
            />
          )}
          {issueDetails.cve && <CVEBadge cve={issueDetails.cve} showTooltip={false} /> }
          {exploitability !== Exploitability.Unknown && <KnownExploitBadge showTooltip={false} />}
        </>
      }
    />
  );
};

export default VulnerabilityIssueDetailsHeader;
